import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Grid, Input, InputAdornment } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import timeZone from "./../../../../../../../@utility/app/timeZone";

// @Utility
import {
    setStorage,
    removeStorage,
    getStorage
} from "../../../../../../../@utility/app/storage";
import alertText from "../../../../../../../@utility/app/alertText";
import validateInput from "../../../../../../../@utility/app/validateInput";

// Auth
import { isUserLoggedIn } from "../../../../../../../@core/auth/utils";

// CSS
import styles from "./../../../../assets/css/Style.module.css";
import modalStyles from "./../../../../assets/css/Modal.module.css";
import buttonStyles from "./../../../../assets/css/Button.module.css";

// Services
import {
    confirmDepositCrypto,
    loadDepositFirst,
    cancenDeposit,
    checkDeposit
} from "../../../../../../../@core/services/depositService";

// Redux
import { setStatusSpinner } from "../../../../../../../@core/redux/actions/app/spinner";
import { setDepositData } from "../../../../../../../@core/redux/actions/deposit/index";
import { setDataAlert } from "../../../../../../../@core/redux/actions/app/alert";

// Component
import Condition from "../../component/Condition";
import SnackbarSet from "../../../../components/SnackbarSet";
import BoxResultPayment from "./../form-action/BoxResultPayment";

// ข้อมูลเหรียญคริปโตและเครือข่าย
import cryptoNetworkData from "json-data/crypto-network.json";

// Icons
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ImgBinance from "images/crypto_icon/icon-exchange/binance.webp";
import ImgBitkub from "images/crypto_icon/icon-exchange/bitkub.webp";

/**
 * ข้อมูล Tab คู่มือฝากคริปโต
 */
const CRYPTO_GUIDE_CONFIG = {
    binance: {
        label: "Binance",
        icon: ImgBinance,
        emptyMessage: "เนื้อหาวิธีการฝากผ่าน Binance",
    },
    bitkub: {
        label: "Bitkub",
        icon: ImgBitkub,
        emptyMessage: "เนื้อหาวิธีการฝากผ่าน Bitkub",
    },
};

/**
 * ตรวจสอบว่าเป็น String ที่มีข้อมูลจริงหรือไม่
 */
const isNonEmptyString = (value) => {
    return typeof value === "string" && value.trim().length > 0;
};

/**
 * ตรวจสอบว่ามีข้อมูลสำหรับแสดงผลหรือไม่
 */
const hasCmsContent = (value) => {
    return (
        React.isValidElement(value) ||
        isNonEmptyString(value) ||
        typeof value === "number"
    );
};

/**
 * เลือกข้อมูลจาก value ก่อน
 * หาก value ว่าง จะใช้ placeholder
 */
const getCmsContent = (item) => {
    if (React.isValidElement(item)) {
        return item;
    }

    if (
        typeof item === "string" ||
        typeof item === "number"
    ) {
        return item;
    }

    if (hasCmsContent(item?.value)) {
        return item.value;
    }

    if (hasCmsContent(item?.placeholder)) {
        return item.placeholder;
    }

    return "";
};

/**
 * ตรวจสอบว่า Content เป็นรูปภาพหรือไม่
 */
const isImageContent = (content) => {
    if (!isNonEmptyString(content)) {
        return false;
    }

    const value = content.trim();

    if (
        value.startsWith("data:image/") ||
        value.startsWith("blob:")
    ) {
        return true;
    }

    return /\.(jpg|jpeg|png|gif|webp|svg|avif|bmp|ico)(?:[?#].*)?$/i.test(
        value
    );
};

/**
 * ตรวจสอบประเภท Content
 */
const getCmsContentType = (content) => {
    if (React.isValidElement(content)) {
        return "element";
    }

    if (!hasCmsContent(content)) {
        return "empty";
    }

    if (isImageContent(content)) {
        return "image";
    }

    return "text";
};

/**
 * แปลง CMS ให้เป็น Array รูปแบบเดียวกัน
 *
 * รองรับแบบ Object:
 *
 * {
 *   "section-crypto-binance-howto-01": "https://..."
 * }
 *
 * รองรับแบบ Object ที่มีรายละเอียด:
 *
 * {
 *   "section-crypto-binance-howto-01": {
 *      value: "https://...",
 *      placeholder: ""
 *   }
 * }
 *
 * รองรับแบบ Array:
 *
 * [
 *   {
 *      name: "section-crypto-binance-howto-01",
 *      value: "https://...",
 *      placeholder: ""
 *   }
 * ]
 */
const normalizeCmsItems = (cms) => {
    if (!cms) {
        return [];
    }

    if (Array.isArray(cms)) {
        return cms.map((item) => {
            const content = getCmsContent(item);

            return {
                name: item?.name || "",
                content,
                contentType: getCmsContentType(content),
            };
        });
    }

    if (typeof cms === "object") {
        return Object.entries(cms).map(([name, item]) => {
            const content = getCmsContent(item);

            return {
                name,
                content,
                contentType: getCmsContentType(content),
            };
        });
    }

    return [];
};

/**
 * ดึงข้อมูลคู่มือตาม Exchange
 *
 * รองรับ:
 *
 * section-crypto-binance-howto
 * section-crypto-binance-howto-01
 * section-crypto-binance-howto-02
 * section-crypto-binance-howto-03
 *
 * และเรียงตามเลขท้ายอัตโนมัติ
 */
const getCryptoGuideContents = (cmsItems, exchange) => {
    const prefix = `section-crypto-${exchange}-howto`;

    return cmsItems
        .filter((item) => {
            const name = item?.name?.trim();

            if (!name || item?.contentType === "empty") {
                return false;
            }

            return (
                name === prefix ||
                name.startsWith(`${prefix}-`)
            );
        })
        .map((item) => {
            const name = item.name.trim();
            const orderMatched = name.match(/-(\d+)$/);

            return {
                ...item,
                name,
                order: orderMatched
                    ? Number(orderMatched[1])
                    : 0,
            };
        })
        .sort((a, b) => a.order - b.order);
};

/**
 * แสดงเนื้อหาคู่มือแต่ละรายการ
 */
function CryptoGuideContentItem({
    item,
    exchangeLabel,
    index,
}) {
    const [imageError, setImageError] = useState(false);

    if (item.contentType === "element") {
        return item.content;
    }

    if (item.contentType === "image") {
        if (imageError) {
            return (
                <div className="center textBoldMedium color-text-main">
                    ไม่สามารถโหลดรูปภาพได้
                </div>
            );
        }

        return (
            <img
                className="cyrptoHowToFull"
                src={item.content}
                alt={`วิธีการฝากผ่าน ${exchangeLabel} รูปที่ ${index + 1}`}
                loading={index === 0 ? "eager" : "lazy"}
                onError={() => setImageError(true)}
            />
        );
    }

    return (
        <div
            className="center textBoldMedium color-text-main"
            style={{
                whiteSpace: "pre-line",
                overflowWrap: "anywhere",
            }}
        >
            {String(item.content)}
        </div>
    );
}

export default function FormCrypto({
    typePage,
    depositItems,
    selectBank,
    channelInfo,
    iosPreviewUrl,
    setIosPreviewUrl,
    dataSetting,
}) {
    const dispatch = useDispatch();
    const history = useHistory();
    const childRef = useRef();

    const [money, setMoney] = useState(0);
    const [timeDepositProcess, setTimeDepositProcess] = useState(null);
    const [timeShow, setTimeShow] = useState(0);
    const [promotionSwitch, setPromotionSwitch] = useState(false);
    const [activeTab, setActiveTab] = useState("binance");

    let snackbarJson = {};

    const [coin, setCoin] = useState(
        cryptoNetworkData[0]?.value || "USDT"
    );

    const [network, setNetwork] = useState(
        cryptoNetworkData[0]?.networks[0]?.value || "ERC20"
    );

    /**
     * จัดการข้อมูล CMS คู่มือฝากคริปโต
     */
    const cmsItems = useMemo(
        () => normalizeCmsItems(dataSetting?.cms),
        [dataSetting?.cms]
    );

    const binanceGuideContents = useMemo(
        () => getCryptoGuideContents(cmsItems, "binance"),
        [cmsItems]
    );

    const bitkubGuideContents = useMemo(
        () => getCryptoGuideContents(cmsItems, "bitkub"),
        [cmsItems]
    );

    const activeGuideConfig =
        CRYPTO_GUIDE_CONFIG[activeTab] ||
        CRYPTO_GUIDE_CONFIG.binance;

    const activeGuideContents =
        activeTab === "bitkub"
            ? bitkubGuideContents
            : binanceGuideContents;

    /**
     * ข้อมูลเหรียญและเครือข่ายที่เลือก
     */
    const selectedCoin =
        cryptoNetworkData.find((item) => item.value === coin) ||
        cryptoNetworkData[0];

    const availableNetworks = selectedCoin?.networks || [];

    const selectedNetwork =
        availableNetworks.find((item) => item.value === network) ||
        availableNetworks[0];

    /**
     * เมื่อเปลี่ยนเหรียญ ให้เปลี่ยน Network เป็นตัวแรก
     */
    const handleCoinChange = (event) => {
        const newCoin = event.target.value;

        setCoin(newCoin);

        const newCoinData = cryptoNetworkData.find(
            (item) => item.value === newCoin
        );

        if (newCoinData?.networks?.length > 0) {
            setNetwork(newCoinData.networks[0].value);
        }
    };

    const confirmTrasaction = () => {
        if (!isUserLoggedIn()) {
            return null;
        }

        if (depositItems?.mode === "pay") {
            return null;
        }

        if (selectBank === 0 || selectBank === "0") {
            snackbarJson = {
                statusShow: true,
                statusAlert: "warning",
                titleAlert: "แจ้งเตือน",
                textAlert: "กรุณาเลือกบัญชีเติมเงิน! ก่อนทำรายการ",
            };

            openBox(snackbarJson);
            return true;
        }

        if (money <= 0) {
            snackbarJson = {
                statusShow: true,
                statusAlert: "warning",
                titleAlert: "แจ้งเตือน",
                textAlert: "กรุณาระบุจำนวนที่ต้องการเติมเงิน !",
            };

            openBox(snackbarJson);
            return true;
        }

        if (money < channelInfo?.min_balance) {
            snackbarJson = {
                statusShow: true,
                statusAlert: "warning",
                titleAlert: "แจ้งเตือน",
                textAlert: `ยอดเติมเงินเริ่มต้นที่ ${channelInfo?.min_balance} บาท`,
            };

            openBox(snackbarJson);
            return true;
        }

        if (money > channelInfo?.max_balance) {
            snackbarJson = {
                statusShow: true,
                statusAlert: "warning",
                titleAlert: "แจ้งเตือน",
                textAlert: `ยอดเติมเงินสูงสุด ${channelInfo?.max_balance} บาท`,
            };

            openBox(snackbarJson);
            return true;
        }

        dispatch(
            setStatusSpinner({
                status: true,
                textProcess: "ระบบกำลังทำรายการ กรุณารอสักครู่",
            })
        );

        const myJson = {
            amount: Number(money),
            channelId: channelInfo?.id || 1,
            channel: channelInfo?.key,
            currency: coin,
            network,
            network_short:
                selectedNetwork?.network_short ||
                selectedNetwork?.label,
            txId: "",
        };

        const requestPath =
            "/customer/payment-v3/deposit/submit-crypto";

        confirmDepositCrypto(myJson, requestPath).then((data) => {
            const statusCode = data.code;

            dispatch(
                setStatusSpinner({
                    status: false,
                    textProcess: null,
                })
            );

            if (statusCode === 200 || statusCode === 0) {
                dispatch(setDepositData(data.result));

                setStorage("processDeposit", data.result.id);
                setStorage("paySrc", data?.result?.pay_src);
                setStorage("payMode", data?.result?.pay_mode);
                setStorage(
                    "payAccount",
                    JSON.stringify(data?.result?.pay_account)
                );
                setStorage(
                    "payTimeOut",
                    data?.result?.pay_time_out
                );
                setStorage(
                    "payAmount",
                    data?.result?.pay_amount
                );
                setStorage(
                    "channelPaySelect",
                    JSON.stringify(channelInfo)
                );

                const payAccountData =
                    data.result?.pay_account;

                if (payAccountData?.code) {
                    setStorage(
                        "payAccount",
                        JSON.stringify(data.result?.pay_account)
                    );
                } else {
                    const payResultData =
                        data.result?.payment_result?.account;

                    if (payResultData) {
                        setStorage(
                            "payAccount",
                            JSON.stringify({
                                account: payResultData?.account_no,
                                code: payResultData?.bank_code,
                                name: payResultData?.account_name,
                            })
                        );
                    } else {
                        setStorage(
                            "payAccount",
                            JSON.stringify(data.result?.pay_account)
                        );
                    }
                }

                if (data.result?.pay_mode === "crypto") {
                    setStorage(
                        "paySrc",
                        data.result?.pay_account?.account
                    );
                }

                if (data.result?.payment_result?.crypto_fx_rate) {
                    setStorage(
                        "payRate",
                        data.result.payment_result.crypto_fx_rate
                    );
                }

                if (data.result?.pay_amount) {
                    const finalAmount =
                        data.result?.payment_result?.crypto_amount ||
                        data.result?.pay_amount;

                    setStorage("payAmount", finalAmount);
                }

                if (data.result?.qrcode_time_out) {
                    const firstEventTimestamp = Date.now(
                        data.result?.qrcode_time_out
                    );

                    setStorage(
                        "depositPromptPayTime",
                        firstEventTimestamp
                    );
                } else {
                    const firstEventTimestamp = Date.now();
                    const timeStart =
                        firstEventTimestamp + 5 * 60 * 1000;

                    setStorage(
                        "depositPromptPayTime",
                        timeStart
                    );
                }

                history.push(
                    `/deposit/form/${myJson?.channel}`
                );

                return;
            }

            if (channelInfo?.event_select === "auto") {
                history.push("/deposit/menu");
            }

            if (statusCode === 2 || statusCode === 4) {
                snackbarJson = alertText(statusCode);
                openBox(snackbarJson);

                setTimeout(() => {
                    window.location.href = "/";
                }, 5000);

                return;
            }

            if (statusCode === 36 || statusCode === 79) {
                if (data?.result?.swift_code) {
                    openBox({
                        statusShow: true,
                        statusAlert: "warning",
                        titleAlert: "แจ้งเตือน",
                        textAlert: `สร้างรายการไม่สำเร็จ เนื่องจากไม่สามารถเชื่อมต่อกับผู้ให้บริการ (${data?.result?.swift_code}) <div class="alertMessageError">** หมายเหตุ : ${data?.result?.message} **</div>`,
                    });

                    return;
                }

                snackbarJson = alertText(statusCode);
                openBox(snackbarJson);
                return;
            }

            snackbarJson = alertText(statusCode);
            openBox(snackbarJson);
        });

        return true;
    };

    const openBox = (alertData) => {
        dispatch(
            setDataAlert({
                type: alertData.statusAlert,
                title: alertData.titleAlert,
                text: alertData.textAlert,
                action: true,
                redirect: null,
                show: true,
                buttonText: "ตกลง",
            })
        );
    };

    const reloadData = (page) => {
        loadDepositFirst().then((data) => {
            const statusCode = data.code;

            if (statusCode === 200 || statusCode === 0) {
                if (data?.result?.pay_src) {
                    setStorage(
                        "paySrc",
                        data?.result?.pay_src
                    );
                }

                if (data?.result?.pay_mode) {
                    setStorage(
                        "payMode",
                        data?.result?.pay_mode
                    );
                }

                if (data?.result?.pay_account?.account) {
                    setStorage(
                        "payAccount",
                        JSON.stringify(data?.result?.pay_account)
                    );
                }

                if (data?.result?.pay_amount) {
                    setStorage(
                        "payAmount",
                        data?.result?.pay_amount
                    );
                }

                if (data?.result?.pay_time_out) {
                    setStorage(
                        "payTimeOut",
                        data?.result?.pay_time_out
                    );
                }

                const payAccountData =
                    data.result?.pay_account;

                if (payAccountData?.code) {
                    setStorage(
                        "payAccount",
                        JSON.stringify(data.result?.pay_account)
                    );
                } else {
                    const payResultData =
                        data.result?.payment_result?.account;

                    if (payResultData) {
                        setStorage(
                            "payAccount",
                            JSON.stringify({
                                account: payResultData?.account_no,
                                code: payResultData?.bank_code,
                                name: payResultData?.account_name,
                            })
                        );
                    } else {
                        setStorage(
                            "payAccount",
                            JSON.stringify(data.result?.pay_account)
                        );
                    }
                }

                if (data.result?.pay_mode === "crypto") {
                    setStorage(
                        "paySrc",
                        data.result?.pay_account?.account
                    );
                }

                if (data.result?.payment_result?.crypto_fx_rate) {
                    setStorage(
                        "payRate",
                        data.result.payment_result.crypto_fx_rate
                    );
                }

                if (data.result?.pay_amount) {
                    const finalAmount =
                        data.result?.payment_result?.crypto_amount ||
                        data.result?.pay_amount;

                    setStorage("payAmount", finalAmount);
                }

                if (data.result?.qrcode_time_out) {
                    const firstEventTimestamp = Date.now(
                        data.result?.qrcode_time_out
                    );

                    setStorage(
                        "depositPromptPayTime",
                        firstEventTimestamp
                    );
                } else {
                    const firstEventTimestamp = Date.now();
                    const timeStart =
                        firstEventTimestamp + 5 * 60 * 1000;

                    setStorage(
                        "depositPromptPayTime",
                        timeStart
                    );
                }

                if (data?.result?.payment_result) {
                    setStorage("paymentResult", JSON.stringify(data.result?.payment_result));
                }

                dispatch(setDepositData(data.result));
                setStorage("processDeposit", data.result.id);

                if (page === "home") {
                    history.push("/");
                } else {
                    history.push("/deposit/pending/manual");
                }

                return;
            }

            if (statusCode === 2 || statusCode === 4) {
                snackbarJson = alertText(statusCode);

                childRef.current?.snackbar(snackbarJson);

                setTimeout(() => {
                    window.location.href = "/";
                }, 5000);

                return;
            }

            snackbarJson = alertText(statusCode);
            childRef.current?.snackbar(snackbarJson);
        });
    };

    const cancelTransaction = (noProcess) => {
        dispatch(
            setStatusSpinner({
                status: true,
                textProcess: null,
            })
        );

        cancenDeposit().then((data) => {
            const statusCode = data.code;

            dispatch(
                setStatusSpinner({
                    status: false,
                    textProcess: null,
                })
            );

            if (statusCode === 200 || statusCode === 0) {
                snackbarJson = {
                    statusShow: true,
                    statusAlert: "success",
                    titleAlert: "แจ้งเตือน",
                    textAlert: "ยกเลิกรายการเติมเงินสำเร็จ !",
                };

                removeStorage("paySrc");
                removeStorage("payMode");
                removeStorage("payAccount");
                removeStorage("payAmount");
                removeStorage("payTimeOut");
                removeStorage("processDeposit");
                removeStorage("depositPromptPayTime");
                removeStorage("channelPaySelect");
                removeStorage("payRate");

                if (!noProcess) {
                    openBox(snackbarJson);
                }

                reloadData("home");
                return;
            }

            if (statusCode !== 39 && statusCode !== 0) {
                alertWarning(statusCode);
            }
        });
    };

    const alertWarning = (statusCode) => {
        if (statusCode === 2 || statusCode === 4) {
            snackbarJson = alertText(statusCode);
            openBox(snackbarJson);

            setTimeout(() => {
                window.location.href = "/";
            }, 5000);

            return;
        }

        snackbarJson = alertText(statusCode);
        openBox(snackbarJson);
    };

    const resetNumber = () => {
        if (money === 0) {
            setMoney("");
        }
    };

    const checkTransaction = (checkTime) => {
        if (!depositItems?.id) {
            return;
        }

        checkDeposit(depositItems.id).then((data) => {
            const statusCode = data.code;

            if (statusCode === 200 || statusCode === 0) {
                switch (data.result.status) {
                    case 0:
                    case 1:
                        break;

                    case 2:
                        snackbarJson = {
                            statusShow: true,
                            statusAlert: "success",
                            titleAlert: "แจ้งเตือน",
                            textAlert: "ทำรายการเติมเงินสำเร็จ !",
                        };

                        openBox(snackbarJson);

                        if (checkTime) {
                            clearInterval(checkTime);
                        }

                        removeStorage("processDeposit");
                        reloadData("home");
                        break;

                    case 3:
                        if (checkTime) {
                            clearInterval(checkTime);
                        }

                        snackbarJson = {
                            statusShow: true,
                            statusAlert: "warning",
                            titleAlert: "แจ้งเตือน",
                            textAlert: "ยกเลิกรายการเติมเงิน เนื่องจากมีเงื่อนไขบางอย่างไม่ถูกต้อง! กรุณาทำรายการใหม่อีกครั้ง",
                        };

                        openBox(snackbarJson);
                        removeStorage("processDeposit");

                        if (channelInfo?.event_select === "auto") {
                            reloadData("home");
                        } else {
                            reloadData("/");
                        }

                        break;

                    default:
                        if (checkTime) {
                            clearInterval(checkTime);
                        }

                        history.push("/deposit/menu");
                        removeStorage("processDeposit");
                        break;
                }

                return;
            }

            if (statusCode === 2 || statusCode === 4) {
                snackbarJson = alertText(statusCode);
                openBox(snackbarJson);

                setTimeout(() => {
                    window.location.href = "/";
                }, 5000);

                return;
            }

            snackbarJson = alertText(statusCode);
            openBox(snackbarJson);
        });
    };

    const renderCancelBtn = () => {
        if (
            timeDepositProcess ||
            depositItems?.mode !== "pay"
        ) {
            return null;
        }

        if (
            depositItems?.situation === 2 ||
            depositItems?.slip
        ) {
            return null;
        }

        if (timeDepositProcess !== null) {
            return (
                <div className={styles.boxMarginFormTop}>
                    <div className="center">
                        <button
                            type="button"
                            className={[
                                buttonStyles.btnConfirm,
                                buttonStyles.btnCancel,
                            ].join(" ")}
                            style={{ opacity: "0.6" }}
                        >
                            ยกเลิกเติมเงิน ({timeShow})
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.boxMarginFormTop}>
                <div className="center">
                    <button
                        type="button"
                        className={[
                            buttonStyles.btnConfirm,
                            buttonStyles.btnCancel,
                        ].join(" ")}
                        onClick={() => cancelTransaction(false)}
                    >
                        ยกเลิกเติมเงิน
                    </button>
                </div>
            </div>
        );
    };

    const secondsToTime = (secs) => {
        const divisorForMinutes = secs % (60 * 60);

        let minutes = Math.floor(
            divisorForMinutes / 60
        );

        const divisorForSeconds =
            divisorForMinutes % 60;

        let seconds = Math.ceil(divisorForSeconds);

        minutes =
            minutes < 10
                ? `0${minutes}`
                : minutes;

        seconds =
            seconds < 10
                ? `0${seconds}`
                : seconds;

        setTimeShow(`${minutes} : ${seconds}`);
    };

    useEffect(() => {
        if (!isUserLoggedIn()) {
            return;
        }

        if (depositItems?.mode === "pay") {
            const amountData = parseInt(
                depositItems.amount,
                10
            );

            setMoney(amountData);

            const payAccount = getStorage("payAccount");
            const paySrc = getStorage("paySrc");

            if (
                !payAccount &&
                !paySrc &&
                depositItems?.transaction_uuid
            ) {
                cancelTransaction(true);
            }
        }
    }, [depositItems]);

    useEffect(() => {
        if (!depositItems) {
            return undefined;
        }

        if (depositItems.mode !== "pay") {
            setTimeDepositProcess(null);
            return undefined;
        }

        setTimeDepositProcess(depositItems.time_create);

        let timer = depositItems.time;

        const checkTime = setInterval(() => {
            timer -= 1;

            if (timer < 0) {
                setTimeShow("expired");
            }
        }, 1000);

        return () => {
            clearInterval(checkTime);
        };
    }, [depositItems]);

    useEffect(() => {
        if (timeDepositProcess === null) {
            return undefined;
        }

        const travelTime = moment(timeDepositProcess).add(
            10,
            "minutes"
        );

        const dateEnd = timeZone(
            travelTime,
            "noFormat"
        );

        const dateCurrent = timeZone(
            moment(),
            "noFormat"
        );

        const duration = moment.duration(
            dateEnd.diff(dateCurrent)
        );

        let seconds = duration.asSeconds();

        const checkTime = setInterval(() => {
            seconds -= 1;

            if (seconds <= 0) {
                clearInterval(checkTime);
                setTimeDepositProcess(null);
                setTimeShow("expired");
            } else {
                secondsToTime(seconds);
            }
        }, 1000);

        return () => {
            clearInterval(checkTime);
        };
    }, [timeDepositProcess]);

    useEffect(() => {
        if (!isUserLoggedIn()) {
            return;
        }

        if (
            channelInfo &&
            depositItems?.mode === "deposit"
        ) {
            const eventSelect =
                channelInfo?.event_select;

            if (eventSelect === "auto") {
                setMoney(-1);

                const timeout = setTimeout(() => {
                    confirmTrasaction();
                }, 50);

                return () => {
                    clearTimeout(timeout);
                };
            }
        }
    }, [channelInfo, depositItems]);

    useEffect(() => {
        if (
            depositItems?.mode !== "pay" ||
            !depositItems?.id
        ) {
            return undefined;
        }

        checkTransaction();

        const checkTime = setInterval(() => {
            checkTransaction(checkTime);
        }, 3000);

        return () => {
            clearInterval(checkTime);
        };
    }, [depositItems]);

    return (
        <>
            <SnackbarSet ref={childRef} />

            <div className={modalStyles.boxTitle}>
                {channelInfo?.name}
            </div>

            {depositItems?.mode === "deposit" && (
                <div className={styles.boxMarginFormTop}>
                    <div
                        className={styles.textBankMoney}
                        style={{ color: "white" }}
                    >
                        เลือกเหรียญ :
                    </div>

                    <div className="crypto-mt-15 positionRelative">
                        <div className="cryptoSelectWrapper">
                            <div className="cryptoSelectIconBox">
                                <img
                                    src={selectedCoin?.icon}
                                    alt={selectedCoin?.label}
                                    className="cryptoIconImg"
                                />
                            </div>

                            <select
                                className="selectBankStyles cryptoSelect"
                                value={coin}
                                onChange={handleCoinChange}
                            >
                                {cryptoNetworkData.map((item) => (
                                    <option
                                        key={item.value}
                                        value={item.value}
                                    >
                                        {item.label}
                                    </option>
                                ))}
                            </select>

                            <div className="cryptoSelectArrow">
                                <ExpandMoreIcon />
                            </div>
                        </div>
                    </div>

                    <div
                        className={`${styles.textBankMoney} contentBoxMarginTop`}
                        style={{ color: "white" }}
                    >
                        เลือกเครือข่าย :
                    </div>

                    <div className="crypto-mt-15 positionRelative crypto-mb-12">
                        <div className="cryptoSelectWrapper">
                            <div className="cryptoSelectIconBox">
                                <img
                                    src={selectedNetwork?.icon}
                                    alt={selectedNetwork?.label}
                                    className="cryptoIconImg"
                                />
                            </div>

                            <select
                                className="selectBankStyles cryptoSelect"
                                value={network}
                                onChange={(event) =>
                                    setNetwork(event.target.value)
                                }
                            >
                                {availableNetworks.map((item) => (
                                    <option
                                        key={item.value}
                                        value={item.value}
                                    >
                                        {item.label}
                                    </option>
                                ))}
                            </select>

                            <div className="cryptoSelectArrow">
                                <ExpandMoreIcon />
                            </div>
                        </div>
                    </div>

                    <div
                        id="DepositInput"
                        className={`${[
                            styles.inputBorder,
                            styles.boxMarginMediumTop,
                        ].join(" ")} contentMarginTop02`}
                    >
                        <div
                            className={styles.textBankMoney}
                            style={{ color: "white" }}
                        >
                            จำนวนเงิน :
                        </div>

                        <Grid
                            container
                            alignItems="center"
                            style={{ marginTop: "-8px" }}
                            spacing={1}
                        >
                            <Grid item xs={8} md={9}>
                                <div
                                    className={[
                                        styles.inputMoneyMargin,
                                        "center",
                                    ].join(" ")}
                                >
                                    <Input
                                        id="money"
                                        variant="standard"
                                        fullWidth
                                        type="number"
                                        pattern="[0-9]*"
                                        color="secondary"
                                        className={[
                                            styles.bankTitile,
                                            styles.yellow,
                                            styles.inputAdornment,
                                            styles.transactionInput,
                                        ].join(" ")}
                                        placeholder="ระบุจำนวนเงิน"
                                        value={money}
                                        style={{
                                            borderBottom:
                                                "1px solid white",
                                        }}
                                        onKeyPress={(event) =>
                                            validateInput(
                                                event,
                                                "numberDecimal"
                                            )
                                        }
                                        onChange={(event) =>
                                            setMoney(
                                                event.target.value
                                            )
                                        }
                                        onClick={resetNumber}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <div
                                                    className={[
                                                        styles.bankTitile,
                                                        styles.white,
                                                        styles.inputAdornment,
                                                    ].join(" ")}
                                                >
                                                    บาท
                                                </div>
                                            </InputAdornment>
                                        }
                                    />
                                </div>
                            </Grid>

                            <Grid item xs={4} md={3}>
                                <div
                                    id={`deposit_${channelInfo?.key}_${channelInfo?.mode}`}
                                    className={
                                        buttonStyles.btnReqBank
                                    }
                                    onClick={confirmTrasaction}
                                >
                                    ขอ
                                    {channelInfo?.mode === "account"
                                        ? "เลขบัญชี"
                                        : " QR Code "}
                                </div>
                            </Grid>
                        </Grid>

                        <div
                            className={styles.textBankMoney}
                            style={{
                                marginTop: "4px",
                                fontSize: "14px",
                            }}
                        >
                            ยอดเริ่มต้น{" "}
                            {channelInfo?.min_balance} สูงสุด{" "}
                            {channelInfo?.max_balance} บาท
                        </div>
                    </div>
                </div>
            )}

            {depositItems && (
                <div className={styles.boxMarginFormTop}>
                    {depositItems?.mode === "pay" ? (
                        <BoxResultPayment
                            depositItems={depositItems}
                            openBox={openBox}
                            history={history}
                            timeShow={timeShow}
                            timeDepositProcess={
                                timeDepositProcess
                            }
                            channelInfo={channelInfo}
                            iosPreviewUrl={iosPreviewUrl}
                            setIosPreviewUrl={setIosPreviewUrl}
                            promotionSwitch={promotionSwitch}
                            setPromotionSwitch={
                                setPromotionSwitch
                            }
                        />
                    ) : (
                        <>
                            <div
                                className={
                                    styles.boxBankAgentBorder
                                }
                            >
                                <div className="transactionConditionNew">
                                    <div className="transactionTitleRule">
                                        วิธีการฝากคริปโต
                                    </div>

                                    <div className="contentMarginTop02">
                                        <div className="cryptoGuideTabContainer">
                                            {Object.entries(
                                                CRYPTO_GUIDE_CONFIG
                                            ).map(
                                                ([key, config]) => (
                                                    <div
                                                        key={key}
                                                        className={`cryptoGuideTab ${activeTab ===
                                                                key
                                                                ? "active"
                                                                : ""
                                                            }`}
                                                        onClick={() =>
                                                            setActiveTab(
                                                                key
                                                            )
                                                        }
                                                    >
                                                        <div className="cryptoGuideTabIcon">
                                                            <img
                                                                src={
                                                                    config.icon
                                                                }
                                                                alt={
                                                                    config.label
                                                                }
                                                                className="crypto-img-full-round"
                                                            />
                                                        </div>

                                                        {config.label}
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>

                                    <div className="contentMarginTop03">
                                        <div className="crypto-guide-content-box">
                                            {activeGuideContents.length >
                                                0 ? (
                                                activeGuideContents.map(
                                                    (
                                                        item,
                                                        index
                                                    ) => (
                                                        <CryptoGuideContentItem
                                                            key={
                                                                item.name
                                                            }
                                                            item={
                                                                item
                                                            }
                                                            index={
                                                                index
                                                            }
                                                            exchangeLabel={
                                                                activeGuideConfig.label
                                                            }
                                                        />
                                                    )
                                                )
                                            ) : (
                                                <div className="center textBoldMedium color-text-main">
                                                    {
                                                        activeGuideConfig.emptyMessage
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                className={`${styles.boxBankAgentBorder} ${styles.boxMarginFormTop02}`}
                            >
                                <Condition
                                    typePage={typePage}
                                    condition={
                                        channelInfo?.condition
                                    }
                                />
                            </div>
                        </>
                    )}
                </div>
            )}

            {depositItems?.mode === "pay" &&
                renderCancelBtn()}
        </>
    );
}